import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { AddLocation, Search } from '@material-ui/icons/';
import moment from 'moment';
import { DragEndEvent, LeafletMouseEvent } from 'leaflet';
import { Box, Button, Flex, IconButton, Spinner } from '@chakra-ui/react';

import { 
  sortEntityByName,
  normalizeBic,
  ValidateError, 
  validateInn
} from '@app/utils';
import { useEntityState } from '@app/hooks/use-entity-state';
import AutocompleteField from '@app/components/common/autocomplete-field';
import SelectField from '@app/components/common/select-field';
import TextField from '@app/components/common/text-field';
import { useGuide } from '@app/hooks/use-guide';
import { useAutocomplete, usePaginatedAutocomplete } from '@app/hooks/use-autocomplete';
import FieldView from '@app/components/common/field-view';
import { api } from '@app/api';
import { useAlert } from '@app/hooks/use-alert';
import SimpleMap from '@app/components/common/map/simple-map';
import { ImageDescriptor } from '@app/api/models/image';
import { Bank } from '@app/api/models/outlet';
import { Form } from '@app/components/common/form/form';
import { SwitchField, Txt } from '@app/components/common';
import { FieldComponentProps } from '@app/components/common/types';
import { VisitFrequencySelectField } from '@app/components/common/visit-frequency';
import { ImagesField } from '@app/components/common/images-field';
import DateField from '@app/components/common/date-field';
import { OutletField } from '@app/pages/equipments/dialogs/components/OutletField';

import { validateForm } from './utils/validation';
import { Channel, DDOutletData, MasterOutletFormInstance } from './types';
import { isNullCoords } from './utils/helper';
import { PreviousValue } from './components/previous-value';
import { ContactFields } from './components/contact-fields';
import { RouteNumberField } from './components/route-number-field';
import { PaymentDefermentField } from './components/payment-deferment-field';
import { CorrAccountField } from './components/corr-account-field';
import { SalesSourceTable } from './components/sales-source-table';
import { OutletDistributorsTable } from './components/outlet-distributors-table';
import { useSalesSourcesState } from './hooks/use-sales-sources-state';
import { useOutletDistributorsState } from './hooks/use-outlet-distributors-state';

interface OutletFormProps {
  instance: Partial<MasterOutletFormInstance>;
  origin?: Partial<MasterOutletFormInstance>;
  submitText?: string;
  submitAndSendText?: string;
  onSubmit?: (instance: Partial<MasterOutletFormInstance>) => void;
  onSubmitAndSend?: (instance: Partial<MasterOutletFormInstance>) => void;
  onCancel: () => void;
  onDeclineForm?: () => Promise<void>;
}

const OutletForm: FC<OutletFormProps> = (props) => {
  const { warning, error } = useAlert();
  const {
    instance,
    origin,
    submitText,
    submitAndSendText,
    onSubmit,
    onSubmitAndSend,
    onCancel,
    onDeclineForm,
  } = props;

  const [outlet, onChange, setInstance] =
    useEntityState<MasterOutletFormInstance>(instance);
  const [loading, setLoading] = useState(false);

  const [images, setImages] = useState<ImageDescriptor[]>([]);

  const [
    {
      divisions,
      segments,
      statuses,
      paymentTypes,
      priceLists,
      channels,
      subChannels,
      salesSources,
      roomTypes,
    },
    setGuides,
  ] = useState<Record<string, EntityBase[]>>({
    divisions: [],
    segments: [],
    statuses: [],
    paymentTypes: [],
    priceLists: [],
    channels: [],
    subChannels: [],
    rawChannels: [],
    salesSources: [],
    roomTypes: [],
  });

  const {
    salesSources: outletSalesSources,
    isLoading: isLoadingSalesSources,
    isTouched,
    addSalesSource,
    removeSalesSource,
    updateSalesSource,
  } = useSalesSourcesState(outlet.id);

  const {
    distributors: outletDistributors,
    isLoading: isLoadingOutletDistributors,
    addOutletDistributor,
    updateOutletDistributor,
  } = useOutletDistributorsState({
    outlet_id: outlet.id,
  });

  const getDivision = useGuide('division', undefined, setLoading);
  const getMasterSegment = useGuide(
    'outlet_segment',
    undefined,
    setLoading
  );
  const getChannels = useGuide('outlet_sub_channel', undefined, setLoading);
  const getMasterOutletStatus = useGuide(
    'outlet_status',
    undefined,
    setLoading
  );
  const getPaymentTypes = useGuide('payment_type', undefined, setLoading);
  const getPriceLists = useGuide('price_list', undefined, setLoading);
  const getSalesSources = useGuide('sales_source', undefined, setLoading);
  const getRoomTypes = useGuide('outlet_room_type', undefined, setLoading);
  const getAutocomplete = useAutocomplete();
  const getPaginatedAutocomplete = usePaginatedAutocomplete();

  const corrAccounts = useMemo(() => {
    return (
      outlet.bank?.accounts?.map((account) => ({
        id: account,
        name: '' + account,
      })) || []
    );
  }, [outlet.bank]);

  useEffect(() => {
    const _instance = instance;

    (async () => {
      const bank = _instance.bic
        ? (await getAutocomplete('bank', { search: _instance.bic }))?.[0]
        : null;

      const channels = (await getChannels()) as Channel[];

      const guides = {
        divisions: await getDivision(),
        segments: await getMasterSegment(),
        statuses: await getMasterOutletStatus(),
        paymentTypes: await getPaymentTypes(),
        priceLists: await getPriceLists(),
        rawChannels: channels,
        channels: sortEntityByName(
          channels.reduce<EntityBase[]>((acc, item, index) => {
            if (acc.find(({ name }) => name === item.channel)) {
              return acc;
            }
            return [...acc, { id: index, name: item.channel }];
          }, [])
        ),
        subChannels: sortEntityByName(
          channels.filter(
            ({ channel }) =>
              channel.toUpperCase() === _instance.channel?.name.toUpperCase()
          )
        ),
        roomTypes: await getRoomTypes(),
        salesSources: await getSalesSources(),
      };

      setGuides(guides);

      _instance.bank = bank as Bank;
      _instance.channel = guides.channels.find((item) => item.name.toUpperCase() === _instance.channel?.name.toUpperCase());

      setLoading(false);

      setInstance(_instance);

      if (!_instance?.photos || !_instance?.photos.length) {return;}
      setLoading(true);
      const _images = await Promise.all(
        _instance.photos.map(async ({ image_pid }) => {
          try {
            const { data } = await api.file.get('' + image_pid);
            return {
              image_pid,
              last_update: null,
              src: 'data:image/jpeg;base64,' + data,
            };
          } catch (e) {
            return {
              last_update: null,
              image_pid,
            };
          }
        })
      );

      setImages(_images);
      setLoading(false);
    })();
  }, []);

  const onSubmitForm = (send: boolean) => async () => {
    const { isValid, errors } = await validateForm(instance, outlet, api);
    if (!isValid) {
      warning(Object.values(errors).join('. '));
      return;
    }

    if (isTouched) {
      await api.acb.updateSalesSource({
        outlet_id: instance.id,
        sales: outletSalesSources.map((item) => ({
          sales_outlet_id: item.sales_outlet_id,
          source_id: Number(item.source.id),
        })),
        comment: '',
      });
    }

    const result = {
      ...outlet,
      photos: images.map((item) => ({
        image_pid: item.image_pid,
        content: item.src,
        filename: '0001.jpg',
      })),
    };
    if (send) {
      onSubmitAndSend(result);
      return;
    }
    onSubmit(result);
  };

  const getLegalData = async (inn: string) => {
    const e: ValidateError = { code: 0, message: '' };
    if (!inn || !validateInn(inn, e)) {
      error(e.message);
      return;
    }

    const data: DDOutletData[] = await getAutocomplete('dd/legal', {
      search: inn,
    }) as DDOutletData[];

    if (data.length) {
      return {
        legal_name: data[0].name,
        legal_address: data[0].address || '',
        kpp: data[0].kpp,
        ogrn: data[0].ogrn,
        okpo: data[0].okpo,
      };
    } else {
      error(e.message || 'Проверьте ИНН, не удалось получить данные организации по ИНН');
      return;
    }
  };
  const [isDroppingMapPointer, setIsDroppingMapPointer] = useState(false);
  const onAddCoordClick = useCallback(() => {setIsDroppingMapPointer(!isDroppingMapPointer);}, [isDroppingMapPointer]);
  const onMapClick = useCallback(async (e: LeafletMouseEvent) => {
    if(!isDroppingMapPointer) {return;}
    const { data } = await api.service.geoLocation(e.latlng.lat, e.latlng.lng);
    if (data.data[0]) {
      onChange('address')({
        id: data.data[0].id,
        full_address: data.data[0].full_address,
        city: data.data[0].city,
        address: data.data[0].address,
        region: data.data[0].region,
        lat: data.data[0].lat,
        lon: data.data[0].lon,
        latlng: [e.latlng.lat, e.latlng.lng]
      });
      setIsDroppingMapPointer(false);
    }
  }, [isDroppingMapPointer]);  
  
  const onDragOnMap = useCallback(async (e: DragEndEvent) => {
    if (!e.target._latlng) {return;}
    const { data } = await api.service.geoLocation(e.target._latlng.lat, e.target._latlng.lng);
    if (data.data[0]) {
      onChange('address')({
        id: data.data[0].id,
        full_address: data.data[0].full_address,
        city: data.data[0].city,
        address: data.data[0].address,
        region: data.data[0].region,
        lat: data.data[0].lat,
        lon: data.data[0].lon,
        latlng: [e.target._latlng.lat, e.target._latlng.lng]
      });
    }
  }, []);

  const points = useMemo(
    () => [
      ...(!isNullCoords(outlet.address?.latlng)
        ? [
            {
              type: 'point',
              geometry: {
                type: 'point',
                coordinates: outlet.address.latlng
                  .map(Number) as [number, number],
              },
              properties: {
                draggable: true,
              },
            },
          ]
        : []),
    ],
    [outlet.address, outlet.geocodedAddress]
  );

  if (loading) {
    return <Box
      position="absolute"
      style={{ backgroundColor: 'rgba(255,255,255,0.6)' }}
      top={0}
      left={0}
      width="100%"
      height="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Spinner />
    </Box>;
  }
  
  return (<>
    <Form 
      instance={outlet}   
      onSubmit={onSubmit ? onSubmitForm(false) : undefined}
      submitText={submitText} 
      alternativeColorScheme="red"
      onCancel={onCancel}    
      onChange={onChange}
      alternativeText={submitAndSendText}
      onAlternativeSubmit={onDeclineForm}
    >
      {outlet.outlet_acb_id && <Form.Spacer span={{ md: 12 }} >
        <Flex gap={2}>
          <Txt>Создана по заявке </Txt> 
          <Txt color='brand.500'>#{outlet.outlet_acb_id}</Txt>
          <Txt> от </Txt>
          <Txt color='brand.500'>{outlet.acb_user?.full_name} ({outlet.acb_user?.username})</Txt>
          <Txt> дата создания </Txt>
          <Txt color='brand.500'>{moment(outlet.create_dt).format('DD.MM.YYYY HH:mm')}</Txt>
        </Flex>
      </Form.Spacer>}
      {outlet.id ? 
        <Form.Item
          label="ID"
          field='id'
          component={TextField}
          componentProps={{ disabled: true }}
          span={{ md: 2 }} 
        /> : <Form.Spacer span={{ md: 2 }} />}
      <Form.Item
        label="Предшественник (ТТ)"
        field='parent'
        component={OutletField}
        span={{ md: 5 }}
        readOnly={!onSubmit}
      />      
      <Form.Divider />
      <Form.Spacer span={{ md: 12 }} >
        <SalesSourceTable
          outlet_id={outlet.id}
          salesSources={salesSources}
          outletSalesSources={outletSalesSources}
          isLoading={isLoadingSalesSources}
          onAddSalesSource={addSalesSource}
          onRemoveSalesSource={removeSalesSource}
          onUpdateSalesSource={updateSalesSource}
          readOnly={!onSubmit}
        />
      </Form.Spacer>
      <Form.Spacer span={{ md: 12 }} >
        <OutletDistributorsTable
          distributors={outletDistributors}
          isLoading={isLoadingOutletDistributors}
          onAddOutletDistributor={addOutletDistributor}
          onUpdateOutletDistributor={updateOutletDistributor}
          readOnly={!onSubmit}
        />
      </Form.Spacer>
      <Form.Divider />
      <Form.Item
        required
        label="Юридическое наименование"
        field='legal_name'
        component={TextField}
        span={{ md: 12 }} 
        readOnly={!onSubmit}
      />
      <Form.Item
        required
        label="Наименование"
        field='name'
        component={TextField}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        span={{ md: 12 }}
        readOnly={!onSubmit}
      />
      <Form.Item
        label="Подсеть"
        field='subnet'
        component={TextField}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        span={{ md: 6 }}
        readOnly={!onSubmit}
      />
      <Form.Item
        required
        label="Вывеска"
        field='title'
        component={TextField}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        span={{ md: 6 }}
        readOnly={!onSubmit}
      />
      <Form.Divider />
      <Form.Item
        required
        label="Тип"
        field='room_type'
        component={SelectField}
        componentProps={{
          options: roomTypes,
          clearable: false,
        }}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        span={{ md: 4 }}
        readOnly={!onSubmit}
      />
      <Form.Item
        required
        label="ИНН"
        field='inn'
        component={TextField}
        componentProps={{
          rightElement: <IconButton
            size='small'
            disabled={!onSubmit}
            onClick={async () => {              
              !!onSubmit && setInstance({
                ...outlet,
                ...(await getLegalData(outlet.inn)),
              });
            } } aria-label='search'     
          >
            <Search />
          </IconButton>
        }}
        span={{ md: 4 }}
        readOnly={!onSubmit}
      />
      <Form.Item
        label="ОГРН"
        field='ogrn'
        component={TextField}
        span={{ md: 4 }}
        readOnly={!onSubmit}
      />
      <Form.Item
        required
        label="Дивизион"
        field='division'
        component={SelectField}
        componentProps={{
          options: divisions,
        }}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        span={{ md: 3 }}
        readOnly={!onSubmit}
      />
      <Form.Item
        required
        label="Сегмент"
        field='segment'
        component={SelectField}
        componentProps={{
          options: segments,
        }}
        span={{ md: 3 }}
        readOnly={!onSubmit}
      />
      <Form.Item
        required
        label="Канал"
        field='channel'
        component={SelectField}
        componentProps={{
          options: channels,
        }}
        onChange={(value) => {
          onChange('sub_channel')(null);
          setGuides((guides) => ({
            ...guides,
            subChannels: sortEntityByName(
              guides.rawChannels.filter((item) => {
                return (
                  item.channel?.toUpperCase() ===
                  (value?.name || '').toUpperCase()
                );
              })
            ),
          }));
        }}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        span={{ md: 6 }}
        readOnly={!onSubmit}
      />
      <Form.Item
        label="День визита в ТТ"
        field='visits_day'
        component={VisitFrequencySelectField}
        span={{ md: 6 }}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        readOnly={!onSubmit}
      />
      <Form.Item
        required
        label="Подканал"
        field='sub_channel'
        component={SelectField}
        componentProps={{
          options: subChannels,
        }}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        span={{ md: 6 }}
        readOnly={!onSubmit}
      />
      <Form.Item
        label="Номер маршрута"
        field='route_number'
        component={RouteNumberField}
        componentProps={{
          name: 'route_number',
          valueProperty: 'name',
          onFetchOptions: getPaginatedAutocomplete,
        }}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        span={{ md: 6 }}
        readOnly={!onSubmit}
      />
      <Form.Divider />
      <Form.Item
        required
        label="Юридический адрес"
        field='legal_address'
        component={({ value, ...props }: FieldComponentProps) => <AutocompleteField
          id="address"
          value={value}
          {...props} 
          onChange={(val) => {
            onChange('legal_address')(val.full_address);
          }}       
        />}
        componentProps={{
          name: 'dd/address',
          valueProperty: 'address',
          onFetchOptions: getAutocomplete,
        }}
        span={{ md: 12 }}
        readOnly={!onSubmit}
      />
      <Form.Item
        required
        label="Фактический адрес"
        field='address'
        component={({ value, ...props }: FieldComponentProps) => <AutocompleteField
          id="address"
          value={value?.full_address}
          {...props}        
        />}
        componentProps={{
          name: 'dd/address',
          valueProperty: 'address',
          onFetchOptions: getAutocomplete,
        }}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        span={{ md: 12 }}
        onChange={(value) => {
          onChange('latlng')(value.latlng);
        }}
        readOnly={!onSubmit}
      />
      <Form.Item
        label="Регион"
        field='address.region'
        component={AutocompleteField}
        componentProps={{
          name: 'region',
          valueProperty: 'name',
          onFetchOptions: getAutocomplete,
        }}
        span={{ md: 6 }}
        onChange={(value) => {
          onChange('address')({
            ...outlet.address,
            region: value,
            id: undefined,
          });
        }}
        readOnly={!onSubmit}
      />
      <Form.Item
        label="Город"
        field='address.city'
        component={AutocompleteField}
        componentProps={{
          name: 'city',
          valueProperty: 'name',
          onFetchOptions: getAutocomplete,
        }}
        span={{ md: 6 }}
        onChange={(value) => {
          onChange('address')({
            ...outlet.address,
            city: value,
            id: undefined,
          });
        }}
        readOnly={!onSubmit}
      />
      {/* {!!outlet.geocodedAddress && (<>
        <Form.Spacer span={{ md: 10 }}>
          <FieldView
            title="Адрес определенный по координатам пользователя"
            value={outlet.geocodedAddress.address}
          />
        </Form.Spacer>
        <Form.Spacer span={{ md: 2 }}>
          <Box
            display="flex"
            height="100%"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Button
              variant="outlined"
              onClick={() => {
                onChange('address')(outlet.geocodedAddress);
              }}
            >
              Применить
            </Button>
          </Box>
        </Form.Spacer>
      </>)} */}
      <Form.Spacer span={{ md: 12 }}>
        <Box position="relative" height={200}>
          <SimpleMap
            points={points}
            cursor={isDroppingMapPointer ? 'pointer' : undefined}
            onMapClick={onMapClick}
            onDragOnMap={onDragOnMap}
          />
          <Box
            position="absolute"
            top={3}
            left={3}
            zIndex="1000"
            display="flex"
            flexDirection="column"
            justifyContent='flex-start'
            gap={2}
          >
            {isNullCoords(outlet.address?.latlng) && <IconButton onClick={onAddCoordClick} aria-label="add">
              <AddLocation color={isDroppingMapPointer ? 'secondary' : 'primary'} />
            </IconButton>}
            <Txt>{`${points?.[0]?.geometry?.coordinates?.[0].toFixed(6) || 0}, ${points?.[0]?.geometry?.coordinates?.[1].toFixed(6) || 0}`}</Txt>
          </Box>
        </Box>
      </Form.Spacer>
      <Form.Divider />
      <Form.Item
        label="Прайс-лист"
        field='price'
        component={SelectField}
        componentProps={{
          options: priceLists,
        }}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        span={{ md: 4 }}
        readOnly={!onSubmit}
      />
      <Form.Item
        label="Тип оплаты"
        field='payment_type'
        component={SelectField}
        componentProps={{
          options: paymentTypes,
        }}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        span={{ md: 4 }}
        readOnly={!onSubmit}
      />
      <Form.Item
        label="Отсрочка платежа"
        field='payment_deferment'
        component={PaymentDefermentField}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        span={{ md: 4 }}
        readOnly={!onSubmit}
      />
      {outlet.payment_type?.id === 20 && (<>
        <Form.Divider />
        <Form.Item
          label="КПП"
          field='kpp'
          component={TextField}
          span={{ md: 6 }} 
          readOnly={!onSubmit}
        />
        <Form.Item
          label="ОКПО"
          field='okpo'
          component={TextField}
          span={{ md: 6 }} 
          readOnly={!onSubmit}
        />
        <Form.Item
          label="БИК банка"
          field='bank'
          component={AutocompleteField}
          componentProps={{
            name: 'bank',
            valueProperty: 'name',
            onFetchOptions: getAutocomplete,
            renderOption: (option: Bank) => (
              <Box display="flex">
                <Box
                  style={{ rowGap: '8px' }}
                  display="flex"
                  flexDirection="column"
                >
                  <Txt color="gray.500">
                    БИК {normalizeBic(option.bic)}&nbsp;
                  </Txt>
                  <Txt>{option.name}</Txt>
                  <FieldView
                    title="Адрес"
                    value={(option as Bank)?.address}
                  />
                </Box>
              </Box>
            )
          }}
          span={{ md: 6 }}
          readOnly={!onSubmit}
        />
        <Form.Spacer span={{ md: 6 }}>{outlet.bank?.name || 'Введите БИК банка'}</Form.Spacer>
        <Form.Item
          label="Кор.счет"
          field='corr_account'
          component={CorrAccountField}
          componentProps={{
            options: corrAccounts,
          }}
          span={{ md: 6 }}
          readOnly={!onSubmit}
        />
        <Form.Item
          label="Р/счет"
          field='account'
          component={TextField}
          componentProps={{
            max: 21
          }}
          span={{ md: 6 }} 
          readOnly={!onSubmit}
        />
      </>)}
      <Form.Divider />
      <Form.Item
        label="Директор - ФИО"
        field='director'
        component={TextField}
        span={{ md: 6 }} 
        container={PreviousValue}
        containerProps={{ origin, instance }}
        readOnly={!onSubmit}
      />
      <Form.Item
        label="Tелефон"
        field='phone'
        component={TextField}
        span={{ md: 3 }} 
        container={PreviousValue}
        containerProps={{ origin, instance }}
        readOnly={!onSubmit}
      />
      <Form.Item
        label="Email"
        field='email'
        component={TextField}
        span={{ md: 3 }} 
        container={PreviousValue}
        containerProps={{ origin, instance }}
        readOnly={!onSubmit}
      />
      <Form.OutletItem
        label="Контактное лицо (1)"
        field='contact_person_1'
        component={ContactFields}
        componentProps={{ origin, instance, field: 'contact_person_1' }}
        readOnly={!onSubmit}
      />
      <Form.OutletItem
        label="Контактное лицо (2)"
        field='contact_person_2'
        component={ContactFields}
        componentProps={{ origin, instance, field: 'contact_person_2' }}
        readOnly={!onSubmit}
      />
      <Form.Divider />
      <Form.Spacer span={{ md: 12 }} >
        <ImagesField 
          images={images}
          loading={loading}
          setImages={setImages}
          readOnly={!onSubmit}
        />
      </Form.Spacer>
      {outlet.user?.username && (<>
        <Form.Divider />
        <Form.Spacer span={{ md: 12 }} >
          <FieldView
            title="Заведена заявка"
            value={`${outlet.user?.full_name} (${outlet.user.username})`}
          />
        </Form.Spacer>
      </>)}
      <Form.Divider />
      <Form.Item
        label='Комментарии (макс. 128 символов)'
        field='comment'
        component={TextField}
        span={{ md: 12 }}
        componentProps={{ multiline: true, max: 128 }}
        readOnly={!onSubmit}
      />
      <Form.Divider />
      <Form.Item
        label="Дата открытия"
        field='open_date'
        component={DateField}
        componentProps={{ disabled: !onSubmit }}
        span={{ md: 4 }} 
        readOnly={!onSubmit}
      />
      <Form.Item
        label="Дата закрытия"
        field='close_date'
        component={DateField}
        componentProps={{ disabled: !onSubmit }}
        span={{ md: 4 }} 
        readOnly={!onSubmit}
      />
      <Form.Item
        label="Статус"
        field='status'
        component={SelectField}
        componentProps={{
          options: statuses,
          clearable: false,
        }}
        container={PreviousValue}
        containerProps={{ origin, instance }}
        span={{ md: 4 }}
        readOnly={!onSubmit}
      />
      <Form.Spacer span={{ md: 8 }}/> 
      <Form.Item
        label="Недоступно к посещению"
        field='is_closed_for_visit'
        component={SwitchField}
        span={{ md: 4 }}
        readOnly={!onSubmit}
      />
      <Form.Spacer span={{ md: 12 }} >
        <Box style={{ gap: 8 }} display='flex' flexDirection='column' alignItems='flex-end'> 
          <FieldView title="Количество холодильников:" value={outlet.count_equipment || '0'} direction='row' />
          <FieldView title="Количество стоек:" value={outlet.count_stand || '0'} direction='row' />
        </Box>
      </Form.Spacer>
      <Form.Divider />
      <Form.Spacer span={{ md: 12 }} >
        <Box display='flex' style={{ gap: 16 }}>
          <FieldView title="Торг. представитель ФИО" value={outlet.acb_user?.full_name} />
          <FieldView title="Email" value={outlet.acb_user?.email} />
          <FieldView title="Телефон" value={outlet.acb_user?.phone} />
        </Box>
      </Form.Spacer>
      {outlet.master_user && <Form.Spacer span={{ md: 12 }} >
        <Box display='flex' style={{ gap: 16 }}>
          <FieldView title="Координатор ФИО" value={outlet.master_user?.full_name} />
          <FieldView title="Email" value={outlet.master_user?.email} />
          <FieldView title="Телефон" value={outlet.master_user?.phone} />
        </Box>
      </Form.Spacer>}
      {outlet.updater_user && <Form.Spacer span={{ md: 12 }} >
        <Box display='flex' style={{ gap: 16 }}>
          <FieldView title="Заявитель ФИО" value={outlet.updater_user?.full_name} />
          <FieldView title="Email" value={outlet.updater_user?.email} />
          <FieldView title="Телефон" value={outlet.updater_user?.phone} />
        </Box>
      </Form.Spacer>}
    </Form>
  </>);
};

export default OutletForm;
